import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import i18n from 'i18n-js';
import { usersActions } from 'state/modules/users';
import { Autocomplete } from 'components/FormFields';

const UserStatusSelect = ({
  label,
  variant,
  field: { name, value },
  omitFields,
  form: { errors, touched, setFieldValue },
  disabled,
  onChange,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);

  const { status, fetchingUserStatus } = useSelector((state) => state.users);

  const fetchUsersStatus = useCallback(
    debounce(() => {
      dispatch(usersActions.fetchUsersStatus());
    }, 1000),
    []
  );

  useEffect(() => {
    if (!value) return;
    fetchUsersStatus();
  }, []) // eslint-disable-line

  useEffect(() => {
    if (value || value === '') {
      const defaultVal = status.find((status) => status === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, status]) // eslint-disable-line

  const handleFetchUsersStatus = () => {
    if (status.length === 0 && !fetchingUserStatus && !value)
      dispatch(usersActions.fetchUsersStatus());
  };

  return (
    <Autocomplete
      label={label}
      margin="normal"
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      value={defaultValue}
      onHighlightChange={handleFetchUsersStatus}
      id="UserStatusSelect"
      loading={fetchingUserStatus}
      onChange={(ev, val) => {
        if (onChange) {
          onChange();
        }
        setFieldValue(name, val || '');
      }}
      getOptionSelected={(option, value) => option === value}
      options={status.filter((value) => omitFields.indexOf(value))}
      error={errors && errors[name] && touched && touched[name]}
      onInputChange={(ev, val) => fetchUsersStatus({ keyword: val })}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      getOptionLabel={(option) => i18n.t(`userStatus.${option}`, { defaultValue: option })}
    />
  );
};

UserStatusSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  omitFields: PropTypes.array,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

UserStatusSelect.defaultProps = {
  omitFields: [],
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  variant: 'standard',
  label: 'Select...',
};

export default UserStatusSelect;
